import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { getAuth } from "firebase/auth";
/**
 * AdminTokenLedgerPanel
 * -------------------------------------------------------------
 * Admin-only dashboard for tracking token flows and paying out instructors.
 *
 * EXPECTED SERVER ENDPOINTS (see your server (9).js proposal):
 * GET  /api/admin/ledger/summary?since=ISO&until=ISO
 * POST /api/admin/payout  { adminUid, tokens, rateCentsPerToken, method, ref }
 *
 * If the GET returns 404 or another error, this panel will surface a friendly
 * setup message for you. Once your endpoints are live, the panel will display
 * live data with totals and instructor earnings.
 *
 * Notes:
 * - This component uses only browser fetch and React. No extra deps.
 * - Styling is Tailwind-first (safe to ignore if Tailwind not installed).
 * - All numbers are tokens unless otherwise stated.
 */

export default function AdminTokenLedgerPanel() {
  const [range, setRange] = useState(() => presetRange("last30"));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState("");
  const [payoutDialog, setPayoutDialog] = useState(null); // { adminUid, displayName, tokensDue }
  const abortRef = useRef(null);
  const auth = getAuth();
  const authedFetch = useCallback(async (input, init) => {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in as an admin.");
  const idToken = await user.getIdToken();
  const merged = {
    ...(init || {}),
    headers: {
      ...(init?.headers || {}),
      Authorization: `Bearer ${idToken}`,
    },
  };
  return fetch(input, merged);
}, [auth]);
  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (range?.since) params.set("since", new Date(range.since).toISOString());
    if (range?.until) params.set("until", new Date(range.until).toISOString());
    return params.toString();
  }, [range]);

  useEffect(() => {
    let mounted = true;
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setLoading(true);
    setError("");

    authedFetch(`https://fluency.ispeakwell.ca/api/admin/ledger/summary?${queryString}`, { signal: ac.signal })
      .then(async (r) => {
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return r.json();
      })
      .then((json) => {
        if (!mounted) return;
        setSummary(normalizeSummary(json));
      })
      .catch((e) => {
        if (!mounted || e.name === "AbortError") return;
        setError(
          e?.message ||
            "Could not load ledger summary. Ensure the admin endpoints are deployed."
        );
        setSummary(null);
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
      ac.abort();
    };
  }, [queryString, authedFetch]);

  const filteredAdmins = useMemo(() => {
    if (!summary?.adminEarnings) return [];
    const s = (search || "").trim().toLowerCase();
    if (!s) return summary.adminEarnings;
    return summary.adminEarnings.filter((a) =>
      [a.displayName, a.adminUid]
        .filter(Boolean)
        .some((x) => String(x).toLowerCase().includes(s))
    );
  }, [summary, search]);

  const totals = useMemo(() => {
    return computeHouseTotals(summary?.houseTotals);
  }, [summary]);

  return (
    <div className="p-4 space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold">Token Ledger & Payouts</h2>
        <RangePicker range={range} setRange={setRange} />
      </header>

      {loading && (
        <div className="text-sm opacity-80">Loading ledger summary…</div>
      )}

      {!!error && (
        <SetupHint message={error} />
      )}

      {summary && (
        <>
          <section className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <KPI title="House Inflows" value={fmtTok(totals.inflows)} sub="tokens" />
            <KPI title="House Outflows" value={fmtTok(totals.outflows)} sub="tokens" />
            <KPI title="House Net" value={fmtTok(totals.net)} sub="tokens" emphasize={true} />
            <KPI title="# Instructors" value={String(summary.adminEarnings?.length || 0)} />
          </section>

          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-lg font-medium">Instructor Earnings</h3>
              <div className="flex items-center gap-2">
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or UID"
                  className="px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring w-64"
                />
                <button
                  className="px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
                  onClick={() => exportAdminsCSV(summary.adminEarnings)}
                >
                  Export CSV
                </button>
              </div>
            </div>

            <div className="overflow-auto rounded-xl border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <Th>#</Th>
                    <Th>Instructor</Th>
                    <Th align="right">Earned</Th>
                    <Th align="right">Paid Out</Th>
                    <Th align="right">Due</Th>
                    <Th align="right">Actions</Th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredAdmins.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-4 text-center text-gray-500">
                        No instructors found for this range.
                      </td>
                    </tr>
                  )}

                  {filteredAdmins.map((a, i) => (
                    <tr key={a.adminUid} className="hover:bg-gray-50/40">
                      <Td>{i + 1}</Td>
                      <Td>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600">
                            {abbrev(a.displayName || a.adminUid)}
                          </div>
                          <div>
                            <div className="font-medium">{a.displayName || a.adminUid}</div>
                            <div className="text-xs text-gray-500">{a.adminUid}</div>
                          </div>
                        </div>
                      </Td>
                      <Td align="right">{fmtTok(a.tokensEarned)}</Td>
                      <Td align="right">{fmtTok(a.tokensPaidOut || 0)}</Td>
                      <Td align="right">
                        <span className={Number(a.tokensDue) > 0 ? "text-emerald-700 font-semibold" : "text-gray-600"}>
                          {fmtTok(a.tokensDue)}
                        </span>
                      </Td>
                      <Td align="right">
                        <div className="flex justify-end gap-2">
                          <button
                            className="px-3 py-1.5 rounded-lg bg-black text-white hover:opacity-90 disabled:opacity-40"
                            disabled={Number(a.tokensDue) <= 0}
                            onClick={() => setPayoutDialog({
                              adminUid: a.adminUid,
                              displayName: a.displayName || a.adminUid,
                              tokensDue: a.tokensDue || 0,
                            })}
                          >
                            Record Payout
                          </button>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {payoutDialog && (
        <PayoutModal
          payload={payoutDialog}
          onClose={() => setPayoutDialog(null)}
          onSubmit={async (body) => {
            try {
              const r = await authedFetch("https://fluency.ispeakwell.ca/api/admin/payout", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
              });
              if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
              // refresh
              setPayoutDialog(null);
              // force refetch by bumping the range 1ms
              setRange((prev) => ({ ...prev, until: new Date(prev.until).toISOString() }));
            } catch (e) {
              alert(e?.message || "Payout failed. Check server logs.");
            }
          }}
        />
      )}
    </div>
  );
}

// ---------- UI helpers ----------
function KPI({ title, value, sub, emphasize }) {
  return (
    <div className={`p-4 rounded-2xl border ${emphasize ? "bg-emerald-50 border-emerald-200" : "bg-white border-gray-200"}`}>
      <div className="text-xs uppercase tracking-wide text-gray-500">{title}</div>
      <div className="text-2xl font-semibold">{value}</div>
      {sub && <div className="text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

function Th({ children, align }) {
  return (
    <th className={`px-3 py-2 text-xs font-semibold ${align === "right" ? "text-right" : "text-left"}`}>
      {children}
    </th>
  );
}

function Td({ children, align }) {
  return (
    <td className={`px-3 py-2 ${align === "right" ? "text-right" : "text-left"}`}>
      {children}
    </td>
  );
}

function SetupHint({ message }) {
  return (
    <div className="p-4 border border-amber-300 bg-amber-50 rounded-2xl text-amber-900">
      <div className="font-medium mb-1">Admin ledger endpoint not responding</div>
      <div className="text-sm">{message}</div>
      <ul className="mt-2 text-sm list-disc pl-6 space-y-1">
        <li>Ensure <code>/api/admin/ledger/summary</code> and <code>/api/admin/payout</code> are deployed.</li>
        <li>Confirm your server has access to Firestore Admin SDK and <code>HOUSE_UID</code> env var.</li>
        <li>Reload this panel after deploying.</li>
      </ul>
    </div>
  );
}

function RangePicker({ range, setRange }) {
  const [mode, setMode] = useState(identifyPreset(range));
  const [since, setSince] = useState(dateInputValue(range.since));
  const [until, setUntil] = useState(dateInputValue(range.until));

  useEffect(() => {
    setMode(identifyPreset(range));
    setSince(dateInputValue(range.since));
    setUntil(dateInputValue(range.until));
  }, [range]);

  function applyCustom() {
    const next = {
      since: new Date(since + "T00:00:00Z").toISOString(),
      until: new Date(until + "T23:59:59Z").toISOString(),
    };
    setRange(next);
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        value={mode}
        onChange={(e) => {
          const m = e.target.value;
          setMode(m);
          setRange(presetRange(m));
        }}
        className="px-3 py-2 rounded-lg border border-gray-300"
      >
        <option value="last7">Last 7 days</option>
        <option value="last30">Last 30 days</option>
        <option value="last90">Last 90 days</option>
        <option value="ytd">Year to date</option>
        <option value="all">All time</option>
        <option value="custom">Custom…</option>
      </select>

      {mode === "custom" && (
        <div className="flex items-center gap-2">
          <input type="date" value={since} onChange={(e) => setSince(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300" />
          <span>to</span>
          <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300" />
          <button className="px-3 py-2 rounded-lg bg-black text-white" onClick={applyCustom}>Apply</button>
        </div>
      )}
    </div>
  );
}

function PayoutModal({ payload, onClose, onSubmit }) {
  const [tokens, setTokens] = useState(payload.tokensDue || 0);
  const [rate, setRate] = useState(""); // cents per token (required)
  const [method, setMethod] = useState("e-transfer");
  const [ref, setRef] = useState("");
  const fiat = useMemo(() => {
    const t = Number(tokens) || 0;
    const c = Number(rate) || 0;
    return (t * c) / 100;
  }, [tokens, rate]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="w-[520px] max-w-[92vw] bg-white rounded-2xl shadow-xl p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-lg font-semibold">Record Payout</div>
            <div className="text-sm text-gray-600">{payload.displayName} ({payload.adminUid})</div>
          </div>
          <button className="opacity-70 hover:opacity-100" onClick={onClose}>✕</button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tokens to pay">
              <input
                type="number"
                min={0}
                max={payload.tokensDue || undefined}
                value={tokens}
                onChange={(e) => setTokens(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300"
              />
              <div className="text-xs text-gray-500 mt-1">Due: {fmtTok(payload.tokensDue || 0)}</div>
            </Field>
            <Field label="Rate (cents per token)">
              <input
                type="number"
                min={0}
                step="0.01"
                placeholder="e.g. 5.0"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300"
              />
            </Field>
          </div>

          <Field label="Method">
            <select value={method} onChange={(e) => setMethod(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300">
              <option value="e-transfer">e‑Transfer</option>
              <option value="paypal">PayPal</option>
              <option value="bank">Bank Transfer</option>
              <option value="other">Other</option>
            </select>
          </Field>

          <Field label="Reference / Notes">
            <input
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder="transaction id, memo, etc."
              className="w-full px-3 py-2 rounded-lg border border-gray-300"
            />
          </Field>

          <div className="p-3 rounded-xl bg-gray-50 border border-gray-200 text-sm">
            <div className="flex justify-between"><span>Tokens</span><span>{fmtTok(tokens)}</span></div>
            <div className="flex justify-between"><span>Rate</span><span>{fmtCents(rate)} / token</span></div>
            <div className="flex justify-between font-semibold"><span>Fiat total</span><span>{fmtMoney(fiat)}</span></div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button className="px-3 py-2 rounded-lg border border-gray-300" onClick={onClose}>Cancel</button>
            <button
              className="px-3 py-2 rounded-lg bg-black text-white disabled:opacity-40"
              disabled={!tokens || !rate || Number(tokens) <= 0 || Number(rate) <= 0}
              onClick={() => onSubmit({
                adminUid: payload.adminUid,
                tokens: Number(tokens),
                rateCentsPerToken: Number(rate),
                method,
                ref,
              })}
            >
              Save Payout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-gray-600 mb-1">{label}</div>
      {children}
    </label>
  );
}

// ---------- data formatting & utils ----------
function normalizeSummary(json) {
  // Accept flexible shapes from the server and fill defaults
  const s = json || {};
  s.houseTotals = s.houseTotals || {};
  s.adminEarnings = (s.adminEarnings || []).map((a) => ({
    adminUid: a.adminUid,
    displayName: a.displayName || a.name || null,
    tokensEarned: Number(a.tokensEarned || 0),
    tokensPaidOut: Number(a.tokensPaidOut || 0),
    tokensDue: Number(
      "tokensDue" in a ? a.tokensDue : (Number(a.tokensEarned || 0) - Number(a.tokensPaidOut || 0))
    ),
  }));
  return s;
}

function computeHouseTotals(h) {
  const inflows = Number(h?.inflowsTotal ?? sumValues(h?.inflows));
  const outflows = Number(h?.outflowsTotal ?? sumValues(h?.outflows));
  const net = inflows - outflows;
  return { inflows, outflows, net };
}

function sumValues(obj) {
  if (!obj) return 0;
  return Object.values(obj).reduce((acc, v) => acc + Number(v || 0), 0);
}

function exportAdminsCSV(rows) {
  const header = [
    "Admin UID",
    "Display Name",
    "Tokens Earned",
    "Tokens Paid Out",
    "Tokens Due",
  ];
  const body = (rows || []).map((r) => [
    safeCsv(r.adminUid),
    safeCsv(r.displayName || ""),
    r.tokensEarned,
    r.tokensPaidOut || 0,
    r.tokensDue || 0,
  ]);
  const csv = [header.join(","), ...body.map((row) => row.join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `instructor-earnings-${new Date().toISOString().slice(0,10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function safeCsv(v) {
  if (v == null) return "";
  const s = String(v).replaceAll('"', '""');
  if (/[",\n]/.test(s)) return `"${s}"`;
  return s;
}

function fmtTok(n) {
  const v = Number(n || 0);
  return `${v.toLocaleString()} tok`;
}
function fmtCents(n) {
  const v = Number(n || 0);
  return `${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}¢`;
}
function fmtMoney(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { style: "currency", currency: "USD" });
}
function abbrev(x) {
  const s = String(x || "?");
  const parts = s.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

// ---------- date helpers ----------
function presetRange(key) {
  const now = new Date();
  const end = new Date(now.toISOString()); // immutable copy
  let start;
  switch (key) {
    case "last7":
      start = addDays(end, -6);
      break;
    case "last30":
      start = addDays(end, -29);
      break;
    case "last90":
      start = addDays(end, -89);
      break;
    case "ytd": {
      const y0 = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
      start = y0;
      break;
    }
    case "all":
      start = new Date("2024-01-01T00:00:00Z");
      break;
    case "custom":
      // caller fills dates separately; fall through with a sane default
    default:
      start = addDays(end, -29);
  }
  return { since: start.toISOString(), until: end.toISOString(), key };
}

function identifyPreset(r) {
  if (!r?.key) return "custom";
  return r.key;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setUTCDate(x.getUTCDate() + n);
  return x;
}

function dateInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
